import React, { useMemo, useState } from "react";
import {
  BrowserLogo,
  RUM,
  SyntheticLogo,
} from "../icons-v2";
import {
  BillingPeriod,
  calculatePricingComparison,
  DATADOG_PRICING_COMPARISON,
  getPlanDefaults,
  PLAN_HOST_COUNTS,
  PRICING_YEARLY_MULTIPLIER,
  PricingComparisonBreakdown,
  PricingComparisonCategory,
  PricingComparisonField,
  PricingComparisonMetricCard,
  PricingComparisonResult,
  PricingComparisonSection,
  PricingComparisonUsage,
  PricingIngestionSection,
  PricingPlanId,
} from "./datadog-comparison-data";
import Tooltip from "../tooltip";
import "../styles/datadog-pricing-comparison.module.scss";

type Props = {
  className?: string;
  content?: PricingComparisonSection;
};

const formatPrice = (amount: number, maxDecimals = 2) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(amount);

const formatDisplayPrice = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(amount));

const getDisplayMultiplier = (billingPeriod: BillingPeriod) =>
  billingPeriod === "yearly" ? PRICING_YEARLY_MULTIPLIER : 1;

const getDisplaySuffix = (billingPeriod: BillingPeriod) =>
  billingPeriod === "yearly" ? "/yr" : "/mo";

const scaleBreakdown = (
  breakdown: PricingComparisonBreakdown,
  multiplier: number,
): PricingComparisonBreakdown =>
  Object.fromEntries(
    Object.entries(breakdown).map(([key, value]) => [key, value * multiplier]),
  ) as PricingComparisonBreakdown;

const DataIngestionIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.444444" y="0.444444" width="31.1111" height="31.1111" rx="6.66667" fill="white" />
    <rect x="0.444444" y="0.444444" width="31.1111" height="31.1111" rx="6.66667" stroke="url(#paint0_linear_11920_2284)" stroke-width="0.888889" />
    <path d="M24.2198 11.0522H14.3105" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M11.7927 11.0522H7.7793" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M11.793 12.5538V9.55078" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M19.2646 17.5015V14.4985" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M11.793 22.4493V19.4463" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M16.8879 16H7.7793" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M24.22 16H19.2646" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M24.2198 20.9478H14.3105" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M11.7927 20.9478H7.7793" stroke="#5B5FC7" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round" />
    <defs>
      <linearGradient id="paint0_linear_11920_2284" x1="9.85807" y1="6.22239" x2="20.86" y2="24.7463" gradientUnits="userSpaceOnUse">
        <stop stop-color="#A2A5F4" />
        <stop offset="1" stop-color="#5B5FC7" />
      </linearGradient>
    </defs>
  </svg>
);

const MetricIcon: React.FC<{ icon: PricingComparisonMetricCard["icon"] }> = ({
  icon,
}) => {
  switch (icon) {
    case "rum":
      return <RUM />;
    case "synthetic":
      return <SyntheticLogo />;
    case "browser":
      return <BrowserLogo />;
    default:
      return null;
  }
};

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  className = "",
}: {
  value: T;
  options: { id: T; label: string; hint?: string }[];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div className={`pricing-segmented-control ${className}`.trim()} role="tablist">
      {options.map((option) => {
        const isActive = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`pricing-segment${isActive ? " is-active" : ""}`}
            onClick={() => onChange(option.id)}
          >
            <span>{option.label}</span>
            {option.hint ? <em>{option.hint}</em> : null}
          </button>
        );
      })}
    </div>
  );
}

const PlainInput: React.FC<{
  value: string | number;
  placeholder?: string;
  onChange: (value: string) => void;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}> = ({ value, placeholder, onChange, inputMode = "numeric" }) => (
  <div className="pricing-plain-input">
    <input
      type="text"
      inputMode={inputMode}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const SuffixInput: React.FC<{
  value: string | number;
  placeholder?: string;
  suffix?: string;
  onChange: (value: string) => void;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}> = ({ value, placeholder, suffix, onChange, inputMode = "numeric" }) => (
  <div className="pricing-suffix-input">
    <input
      type="text"
      inputMode={inputMode}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
    {suffix ? <span>{suffix}</span> : null}
  </div>
);

const FieldInfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="7" stroke="#5B6471" strokeWidth="1.2" />
    <path d="M8 7V11" stroke="#5B6471" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5" r="0.75" fill="#5B6471" />
  </svg>
);

const SelectField: React.FC<{
  value: string;
  options: { value?: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => (
  <div className="pricing-select-field">
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option.value ?? option.label} value={option.value ?? ""}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const DataIngestionField: React.FC<{
  field: PricingComparisonField;
  usage: PricingComparisonUsage;
  onChange: (
    id: keyof PricingComparisonUsage,
    value: string | number | boolean,
  ) => void;
  showK8sCheckbox?: boolean;
}> = ({ field, usage, onChange, showK8sCheckbox = false }) => (
  <div className={`pricing-field ${field.className ?? ""}`.trim()}>
    <div className="pricing-field-label-row">
      <label htmlFor={`pricing-${field.id}`}>{field.label}</label>
      {field.tooltip ? (
        <Tooltip content={field.tooltip} position="top">
          <button
            type="button"
            className="pricing-field-info"
            aria-label={field.tooltip}
          >
            <FieldInfoIcon />
          </button>
        </Tooltip>
      ) : null}
    </div>
    {field.type === "select" ? (
      <SelectField
        value={String(usage[field.id])}
        options={field.options ?? []}
        onChange={(value) => onChange(field.id, value)}
      />
    ) : field.inputVariant === "plain" ? (
      <PlainInput
        value={usage[field.id] as number}
        placeholder={field.placeholder}
        onChange={(value) => onChange(field.id, value === "" ? 0 : Number(value))}
      />
    ) : (
      <SuffixInput
        value={usage[field.id] as number}
        placeholder={field.placeholder}
        suffix={field.suffix}
        onChange={(value) => onChange(field.id, value === "" ? 0 : Number(value))}
      />
    )}
    {showK8sCheckbox ? (
      <label className="pricing-k8s-checkbox">
        <input
          type="checkbox"
          checked={usage.k8sHost}
          onChange={(event) => onChange("k8sHost", event.target.checked)}
        />
        <span>Kubernetes Nodes</span>
      </label>
    ) : null}
  </div>
);

const INGESTION_SECTIONS: {
  id: PricingIngestionSection;
  label: string;
}[] = [
  { id: "infrastructure", label: "Infrastructure" },
  { id: "logs", label: "Logs Data" },
  { id: "apm", label: "Application Performance Monitoring" },
];

const StackedCostBar: React.FC<{
  monthlyTotal: number;
  billingPeriod: BillingPeriod;
  maxTotal: number;
  breakdown: PricingComparisonBreakdown;
  categories: PricingComparisonCategory[];
}> = ({ monthlyTotal, billingPeriod, maxTotal, breakdown, categories }) => {
  const displayMultiplier = getDisplayMultiplier(billingPeriod);
  const displayTotal = monthlyTotal * displayMultiplier;
  const displayBreakdown = scaleBreakdown(breakdown, displayMultiplier);
  const barWidth = maxTotal > 0 ? (displayTotal / maxTotal) * 100 : 0;

  return (
    <div className="pricing-cost-bar">
      <div className="pricing-cost-bar-fill" style={{ width: `${barWidth}%` }}>
        {categories.map((category) => {
          const amount = displayBreakdown[category.id] ?? 0;
          if (amount <= 0 || displayTotal <= 0) return null;
          return (
            <span
              key={category.id}
              style={{
                width: `${(amount / displayTotal) * 100}%`,
                backgroundColor: category.color,
              }}
            />
          );
        })}
      </div>
      <div className="pricing-cost-bar-labels">
        <strong>
          {formatDisplayPrice(displayTotal)}
          {getDisplaySuffix(billingPeriod)}
        </strong>
        
      </div>
    </div>
  );
};

const DatadogPricingComparison: React.FC<Props> = ({
  className = "",
  content = DATADOG_PRICING_COMPARISON,
}) => {
  const [activePlan, setActivePlan] = useState<PricingPlanId>("startup");
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [usage, setUsage] = useState<PricingComparisonUsage>(() =>
    getPlanDefaults("startup"),
  );
  const [result, setResult] = useState<PricingComparisonResult>(() =>
    calculatePricingComparison(getPlanDefaults("startup"), "monthly"),
  );
  const [resultBillingPeriod, setResultBillingPeriod] =
    useState<BillingPeriod>("monthly");
  const [showResults, setShowResults] = useState(true);

  const infoBanner = useMemo(() => {
    const hosts = Math.max(usage.infraHosts, usage.apmHosts);
    return content.infoBannerTemplate
      .replace("{hosts}", String(hosts))
      .replace("{infraCost}", formatPrice(result.datadogInfraCost))
      .replace("{apmCost}", formatPrice(result.datadogApmCost))
      .replace("{datadogInfraRate}", formatPrice(result.datadogInfraRate))
      .replace("{datadogApmRate}", formatPrice(result.datadogApmRate))
      .replace("{middlewareApmRate}", formatPrice(result.middlewareApmRate))
      .replace(
        "{middlewareApmCost}",
        formatPrice(result.middlewareApmCost),
      );
  }, [content.infoBannerTemplate, result, usage.apmHosts, usage.infraHosts]);

  const updateUsage = (
    id: keyof PricingComparisonUsage,
    value: string | number | boolean,
  ) => {
    setUsage((current) => ({ ...current, [id]: value }));
  };

  const handlePlanChange = (planId: PricingPlanId) => {
    setActivePlan(planId);
    const hosts = PLAN_HOST_COUNTS[planId];
    setUsage((current) => ({
      ...current,
      infraHosts: hosts.infraHosts,
      apmHosts: hosts.apmHosts,
    }));
  };

  const getSectionFields = (section: PricingIngestionSection) =>
    content.dataIngestionFields.filter((field) => field.section === section);

  const handleCalculate = () => {
    setResult(calculatePricingComparison(usage, billingPeriod));
    setResultBillingPeriod(billingPeriod);
    setShowResults(true);
  };

  const displayMultiplier = getDisplayMultiplier(resultBillingPeriod);
  const maxTotal = Math.max(
    result.datadog.total * displayMultiplier,
    result.middleware.total * displayMultiplier,
    1,
  );

  const savingsLabel =
    resultBillingPeriod === "yearly"
      ? (content.savingsLabelYearly ?? "You'd save, every year")
      : content.savingsLabel;

  const displaySavings = result.savingsAmount * displayMultiplier;

  return (
    <section
      className={`datadog-pricing-comparison ${className}`.trim()}
      id="pricing-comparison"
      aria-label="Middleware vs Datadog pricing comparison"
    >
      <div className="container">

        <div className="section-tag">
          Pricing Comparison
        </div>
        <div className="d-flex pricing-comparison-header">
          <h2><span className="text-gradient">No Surprise</span> Invoices, Ever</h2>
          <p>Datadog calculates your estimated observability spend based on hosts, log volume, metrics, traces, and APM usage. Middleware calculates bills based on GBs ingested, not hosts.</p>
        </div>

        <div className="pricing-comparison-controls">
          <SegmentedControl<PricingPlanId>
            value={activePlan}
            className="pricing-plan-control"
            options={content.plans.map((plan) => ({
              id: plan.id,
              label: plan.label,
              hint: plan.hint,
            }))}
            onChange={handlePlanChange}
          />
          <SegmentedControl<BillingPeriod>
            value={billingPeriod}
            className="pricing-billing-control"
            options={content.billingOptions.map((option) => ({
              id: option.id,
              label: option.label,
            }))}
            onChange={setBillingPeriod}
          />
        </div>

        <div className="pricing-comparison-shell">
          <article className="pricing-ingestion-card">
            <div className="pricing-ingestion-header">
              <span className="pricing-card-icon" aria-hidden="true">
                <DataIngestionIcon />
              </span>
              <h3>{content.dataIngestionTitle}</h3>
            </div>

            <div className="pricing-ingestion-body">
              <div className="pricing-ingestion-column pricing-ingestion-column-left">
                {INGESTION_SECTIONS.filter(({ id }) => id !== "apm").map(
                  ({ id, label }) => (
                    <section
                      className="pricing-ingestion-section"
                      key={id}
                      aria-label={label}
                    >
                      <p className="pricing-ingestion-section-label">{label}</p>
                      <div className="pricing-ingestion-section-fields">
                        {getSectionFields(id).map((field) => (
                          <DataIngestionField
                            key={field.id}
                            field={field}
                            usage={usage}
                            showK8sCheckbox={field.id === "infraHosts"}
                            onChange={updateUsage}
                          />
                        ))}
                      </div>
                    </section>
                  ),
                )}
              </div>

              <div
                className="pricing-ingestion-divider"
                aria-hidden="true"
              />

              <div className="pricing-ingestion-column pricing-ingestion-column-right">
                {INGESTION_SECTIONS.filter(({ id }) => id === "apm").map(
                  ({ id, label }) => (
                    <section
                      className="pricing-ingestion-section"
                      key={id}
                      aria-label={label}
                    >
                      <p className="pricing-ingestion-section-label">{label}</p>
                      <div className="pricing-ingestion-section-fields pricing-ingestion-section-fields-apm">
                        {getSectionFields(id).map((field) => (
                          <DataIngestionField
                            key={field.id}
                            field={field}
                            usage={usage}
                            onChange={updateUsage}
                          />
                        ))}
                      </div>
                    </section>
                  ),
                )}
              </div>
            </div>
          </article>

          <div className="pricing-metric-grid">
            {content.metricCards.map((card) => (
              <article
                className={`pricing-metric-card${card.fullWidth ? " pricing-metric-card-full-width" : ""}${card.className ? ` ${card.className}` : ""}`.trim()}
                key={card.id}
              >
                <div className="pricing-metric-card-head">
                  <span className="pricing-card-icon" aria-hidden="true">
                    <MetricIcon icon={card.icon} />
                  </span>
                  <h4>{card.title}</h4>
                </div>
                <SuffixInput
                  value={usage[card.id] as number}
                  placeholder={card.placeholder}
                  suffix={card.suffix}
                  onChange={(value) =>
                    updateUsage(card.id, value === "" ? 0 : Number(value))
                  }
                />
              </article>
            ))}
          </div>

          <div className="pricing-info-banner" role="note">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="#5B5FC7" strokeWidth="1.2" />
              <path d="M8 7V11" stroke="#5B5FC7" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="8" cy="5" r="0.75" fill="#5B5FC7" />
            </svg>
            <p>Middleware bills by GB ingested, while Datadog bills by host count. For comparison, we assume 45 GB per APM host, 5 GB per VM host, and 10 GB per Kubernetes node. Actual usage may vary.</p>
          </div>

          <div className="pricing-info-banner pricing-info-banner-secondary" role="note">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="#5B5FC7" strokeWidth="1.2" />
              <path d="M8 7V11" stroke="#5B5FC7" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="8" cy="5" r="0.75" fill="#5B5FC7" />
            </svg>
            <p>{infoBanner}</p>
          </div>

          <div className="pricing-calculate-wrap">
            <button type="button" className="pricing-calculate-btn" onClick={handleCalculate}>
              {content.calculateLabel}
            </button>
          </div>

          {showResults ? (
            <article className="pricing-results-card">
              <div className="pricing-results-summary">
                <p>{savingsLabel}</p>
                <strong>{formatDisplayPrice(displaySavings)}</strong>
                
              </div>

              <div className="pricing-results-divider" aria-hidden="true" />

              <div className="pricing-results-chart">
                <div className="pricing-results-row">
                  <span>Datadog</span>
                  <StackedCostBar
                    monthlyTotal={result.datadog.total}
                    billingPeriod={resultBillingPeriod}
                    maxTotal={maxTotal}
                    breakdown={result.datadog.breakdown}
                    categories={content.categories}
                  />
                </div>
                <div className="pricing-results-row">
                  <span>Middleware</span>
                  <StackedCostBar
                    monthlyTotal={result.middleware.total}
                    billingPeriod={resultBillingPeriod}
                    maxTotal={maxTotal}
                    breakdown={result.middleware.breakdown}
                    categories={content.categories}
                  />
                </div>

                <ul className="pricing-results-legend">
                  {content.categories.map((category) => (
                    <li key={category.id}>
                      <span style={{ backgroundColor: category.color }} />
                      {category.label}
                    </li>
                  ))}
                </ul>

                
              </div>
            </article>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default DatadogPricingComparison;
